import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import CardHolderNameField from '../../../../app/common/fields/card/CardHolderNameField';
import PayVaultCardNumberField from './payVaultFields/PayVaultCardNumberField';
import PayVaultExpiryDateField from './payVaultFields/PayVaultExpiryDateField';
import ExpiryDateField from '../../../../app/common/fields/card/ExpiryDateField';
import { CardFormatterService } from '../card/cardFormatter.service';
import { CardBrandIcon } from '../../../../app/common/fields/card/CardBrandIcon';
import { getCardBrandFromAPIName } from '../cardBrands/cardBrandHelpers';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import CardNumberInputField from './CardNumberInputField';
import { PayVaultAPIService } from '../../../../api/payVault/payVaultAPI';
import ButtonOutline from '../../../../app/common/components/ButtonOutline';
import ICONS from '../../../../app/common/constants/icons';
import { COLORS } from '../../../../app/common/constants/styles';

interface ICardFieldsWrapper {
    show: boolean;
}

const FieldsWrapper = styled.div<ICardFieldsWrapper>`
    & > .field {
        margin-top: 50px;
        margin-bottom: 40px;
        display: ${(props) => (props.show ? `block` : 'none')};
        display: ${(props) => (props.show ? `block` : 'none')};
    }
`;

const BtnControl = styled.div`
    margin: 20px auto;
    display: flex;
    justify-content: center;
`;

export interface ICardFormValues {
    cardNumber?: string;
    brandName: string;
    isPrimary: boolean;
    expireMonth: string;
    expireYear: string;
    ownerName?: string;
    numberLast?: string;
}

interface ICardFormField {
    autoFocus: boolean;
    values: ICardFormValues;
    isEditing?: boolean;
    cardFields: any;
    isPayVaultLoaded: boolean;
    region?: string;
}

const placeHolderShimmer = keyframes`
    0% {
        background-position: 100% 0;
    }

    100% {
        background-position: -100% 0;
    }
`;

const PlaceholderItem = styled.div`
    border: none;
    line-height: normal;
    min-height: 31px;
    width: 100%;

    animation-duration: 1.5s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: ${placeHolderShimmer};
    animation-timing-function: linear;
    background: #fff;
    background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
    background-size: 250% 250%;
`;

const PlaceholderBox = styled.div`
    margin-top: 50px;
    margin-bottom: 40px;
    display: flex;
    div:nth-child(2n) {
        margin-left: 10px;
    }
`;

const PlaceholderLoading = () => {
    return (
        <>
            <div>
                <PlaceholderBox>
                    <PlaceholderItem />
                </PlaceholderBox>
                <PlaceholderBox>
                    <PlaceholderItem />
                </PlaceholderBox>
                <PlaceholderBox>
                    <PlaceholderItem />
                    <PlaceholderItem />
                </PlaceholderBox>
            </div>
        </>
    );
};

export const CardFields = ({
    values,
    autoFocus = false,
    isEditing = false,
    cardFields,
    isPayVaultLoaded,
    region,
}: ICardFormField) => {
    useEffect(() => {
        if (isPayVaultLoaded) {
            PayVaultAPIService.showScanBtn();
        }
    }, [isPayVaultLoaded]);

    const creditCardFields = isEditing ? (
        <>
            <CardNumberInputField
                labelMessage={TranslationHelpers.getMessageDescriptor('label.card_number')}
                id="cardNumberHidden"
                value={CardFormatterService.getLast4Digits(`************${values.numberLast}`)}
                required={true}
                className="field"
            >
                <CardBrandIcon cardBrand={getCardBrandFromAPIName(values.brandName)} />
            </CardNumberInputField>
            <ExpiryDateField
                monthValue={values.expireMonth}
                yearValue={values.expireYear}
                required={true}
            />
        </>
    ) : (
        <>
            <div className="field">
                <PayVaultCardNumberField key="carNumber" />
            </div>
            <div className="field">
                <PayVaultExpiryDateField key="carDate" />
            </div>
        </>
    );

    return (
        <>
            {!isEditing && (
                <BtnControl id="BtnControl" hidden>
                    <ButtonOutline
                        id="scanCardCamera"
                        type="button"
                        text={TranslationHelpers.getMessageDescriptor('button.scanCard')}
                        loading={false}
                        disabled={false}
                        icon={ICONS.CAMERA}
                        onClick={() => PayVaultAPIService.onScanBtnClick()}
                        color={COLORS.rakutenRed}
                        style={{ width: '100%', height: '2.5rem' }}
                    />
                </BtnControl>
            )}
            {!isPayVaultLoaded && !isEditing && <PlaceholderLoading />}
            {!isPayVaultLoaded && !isEditing && <PlaceholderLoading />}
            <FieldsWrapper show={isEditing || isPayVaultLoaded}>
                <CardHolderNameField
                    autoFocus={autoFocus}
                    value={values.ownerName}
                    required={true}
                    className="field"
                    region={region}
                />
                {creditCardFields}
            </FieldsWrapper>
        </>
    );
};
